"use client";

import Link from "next/link";
import {
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import {
  IconArrowRight,
  IconArrowUpRight,
  IconCpu,
  IconScan,
  IconSliders,
  IconTarget,
  type IconProps,
} from "@/app/_components/icons";
import { InstallSnippet } from "@/components/photon/install-snippet";
import { BENCH_ENV } from "./performance-data";
import {
  COLD_STARTS,
  COLD_START_RANGE,
  MODEL_FAMILIES,
  THROUGHPUT_RESULT,
  formatMatrixDate,
  type ModelFamily,
} from "./photon-data";
import { UPDATES, UPDATE_TYPE_LABELS } from "./updates-data";

const QUICKSTART_URL = "https://docs.moondream.ai/running-locally";
const LAUNCH_URL = "/blog/photon-2-launch";
const PERFORMANCE_URL = "/photon/performance";
const SUPPORT_URL = "/photon/support";
const UPDATES_URL = "/photon/updates";

// The four operating characteristics Photon is built around.
const OPERATING_CHARACTERISTICS = [
  {
    Icon: IconSliders,
    title: "Small batches",
    body: "Fast responses for one or a few live streams.",
  },
  {
    Icon: IconTarget,
    title: "Low latency",
    body: "Built for systems that act on the next frame.",
  },
  {
    Icon: IconCpu,
    title: "Less memory",
    body: "Fit models into practical deployment limits.",
  },
  {
    Icon: IconScan,
    title: "Runs locally",
    body: "Keep inference close to cameras, robots, and machines.",
  },
] as const;

const COMPILER_FLOW = [
  {
    kicker: "Input",
    title: "Model graph",
    detail: "Moondream, Qwen, or Gemma",
    core: false,
  },
  {
    kicker: "Compile",
    title: "Photon compiler",
    detail: "Cross-operator optimization",
    core: false,
  },
  {
    kicker: "Generate",
    title: "Target-specific megakernel",
    detail: "One GPU program per model × chip",
    core: true,
  },
  {
    kicker: "Serve",
    title: "Photon runtime",
    detail: "Requests, tokenization, decode on CPU",
    core: false,
  },
] as const;

const MegakernelVisual = () => (
  <div
    className="photon-megakernel-visual"
    role="img"
    aria-label="Model operators compiled through three signal paths into one target-specific GPU megakernel"
  >
    <div className="photon-compiler-formula mono" aria-hidden="true">
      <span>model graph</span>
      <i>×</i>
      <span>GPU target</span>
      <i>→</i>
      <strong>1 megakernel</strong>
    </div>
    <div className="photon-megakernel-stage" aria-hidden="true">
      <div className="photon-operator-stack mono">
        {[
          ["01", "attention"],
          ["02", "mlp"],
          ["03", "norm"],
        ].map(([index, label], nodeIndex) => (
          <span
            className="photon-operator-node"
            key={label}
            style={{ "--operator-index": nodeIndex } as CSSProperties}
          >
            <i>{index}</i>
            {label}
          </span>
        ))}
      </div>
      <div className="photon-compile-buses">
        {Array.from({ length: 3 }, (_, index) => (
          <span
            key={index}
            style={{ "--bus-index": index } as CSSProperties}
          >
            <i />
          </span>
        ))}
      </div>
      <div className="photon-megakernel-package">
        <div className="photon-chip-pins is-horizontal" />
        <div className="photon-chip-pins is-vertical" />
        <div className="photon-megakernel-chip">
          <div className="photon-megakernel-grid">
            {Array.from({ length: 30 }, (_, index) => (
              <i
                key={index}
                style={{ "--kernel-index": index } as CSSProperties}
              />
            ))}
          </div>
          <span className="mono">megakernel</span>
        </div>
      </div>
      <div className="photon-kernel-readout mono">
        <span>target</span>
        <strong>H100</strong>
        <i />
        <span>programs</span>
        <strong>01</strong>
      </div>
    </div>
  </div>
);

const SectionHeader = ({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: string;
  sub?: string;
}) => (
  <div>
    <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
      {eyebrow}
    </div>
    <h2 className="t-heading-xl" style={{ marginTop: 10, maxWidth: 720 }}>
      {title}
    </h2>
    {sub ? (
      <p
        className="t-body-lg"
        style={{ color: "var(--text-secondary)", marginTop: 14, maxWidth: 640 }}
      >
        {sub}
      </p>
    ) : null}
  </div>
);

const CharacteristicCard = ({
  Icon,
  title,
  body,
}: {
  Icon: (props: IconProps) => ReactNode;
  title: string;
  body: string;
}) => (
  <div className="photon-operating-item">
    <div className="concern-icon">
      <Icon size={20} />
    </div>
    <div className="t-heading-sm">{title}</div>
    <p
      className="t-body-md"
      style={{ color: "var(--text-secondary)", marginTop: 6 }}
    >
      {body}
    </p>
  </div>
);

const FamilyCard = ({
  family,
  index,
}: {
  family: ModelFamily;
  index: number;
}) => (
  <div className="model-card photon-family-card">
    <div className="model-card-head">
      <h3>
        <Link href={`/photon/models/${family.id}`} className="family-name-link">
          {family.name}
        </Link>
      </h3>
      <span className="photon-family-index mono tabular">
        {String(index + 1).padStart(2, "0")}
      </span>
    </div>
    <p>{family.description}</p>
    <div
      className="model-card-skills"
      aria-label={`${family.name} supported capabilities`}
    >
      {family.capabilities.map((capability) => (
        <span key={capability} className="is-active">
          {capability}
        </span>
      ))}
    </div>
    <dl className="family-meta">
      {family.releases.length > 0 ? (
        <div>
          <dt>Releases</dt>
          <dd className="family-releases">
            {family.releases.map((release) => (
              <span key={release.name} className="is-active">
                {release.name}
              </span>
            ))}
          </dd>
        </div>
      ) : null}
      {family.versions.length > 0 ? (
        <div>
          <dt>Sizes</dt>
          <dd>{family.versions.join(" · ")}</dd>
        </div>
      ) : null}
      <div>
        <dt>Modalities</dt>
        <dd>{family.modalities}</dd>
      </div>
      <div>
        <dt>Verified hardware</dt>
        <dd>
          {family.verifiedHardware.length}{" "}
          {family.verifiedHardware.length === 1 ? "target" : "targets"} ·{" "}
          {family.verifiedHardware.length <= 3
            ? family.verifiedHardware.join(", ")
            : `${family.verifiedHardware.slice(0, 2).join(", ")} +${
                family.verifiedHardware.length - 2
              } more`}
        </dd>
      </div>
    </dl>
    <div className="model-card-cta" style={{ gap: 18 }}>
      <Link href={SUPPORT_URL} className="family-cta">
        Support details <IconArrowRight size={13} />
      </Link>
      <Link href={PERFORMANCE_URL} className="family-cta">
        Benchmarks <IconArrowRight size={13} />
      </Link>
    </div>
  </div>
);

const PhysicalLoopVisual = () => (
  <div
    className="photon-live-loop"
    aria-label="Physical AI signal path from camera frames through Photon to machine actions"
  >
    <div className="photon-live-head mono">
      <span>Live control loop</span>
      <span className="photon-live-state">
        <i aria-hidden="true" /> Local · online
      </span>
    </div>
    <div className="photon-live-stage">
      <span className="photon-live-index mono">01</span>
      <span className="photon-live-icon" aria-hidden="true">
        <IconScan size={21} />
      </span>
      <span className="photon-live-label">
        <small className="mono">Observe</small>
        <strong>Camera frames</strong>
      </span>
      <span className="photon-live-wave" aria-hidden="true">
        {[18, 42, 28, 66, 36, 78, 52, 26, 58, 34, 72, 44].map(
          (height, index) => (
            <i key={`${height}-${index}`} style={{ height: `${height}%` }} />
          ),
        )}
      </span>
    </div>
    <div className="photon-live-link mono" aria-hidden="true">
      <span>batch 1–8</span>
      <i />
      <IconArrowRight size={14} />
    </div>
    <div className="photon-live-stage photon-live-core">
      <span className="photon-live-index mono">02</span>
      <span className="photon-live-icon" aria-hidden="true">
        <IconCpu size={21} />
      </span>
      <span className="photon-live-label">
        <small className="mono">Infer</small>
        <strong>Compiled Photon program</strong>
      </span>
      <span className="photon-kernel-field" aria-hidden="true">
        {Array.from({ length: 18 }, (_, index) => (
          <i key={index} className={index === 10 ? "is-active" : ""} />
        ))}
      </span>
    </div>
    <div className="photon-live-link mono" aria-hidden="true">
      <span>next frame</span>
      <i />
      <IconArrowRight size={14} />
    </div>
    <div className="photon-live-stage">
      <span className="photon-live-index mono">03</span>
      <span className="photon-live-icon" aria-hidden="true">
        <IconTarget size={21} />
      </span>
      <span className="photon-live-label">
        <small className="mono">Act</small>
        <strong>Machine response</strong>
      </span>
      <span className="photon-action-field mono" aria-hidden="true">
        <i>inspect</i>
        <i>pick</i>
        <i>stop</i>
      </span>
    </div>
    <div className="photon-loop-return mono" aria-hidden="true">
      <span>feedback</span>
      <i />
    </div>
  </div>
);

const BenchmarkInstrument = ({
  coldStartMax,
  coldStartSummary,
}: {
  coldStartMax: string;
  coldStartSummary: string;
}) => {
  const scaleMax = Math.max(
    ...THROUGHPUT_RESULT.comparisons.map(
      (comparison) => comparison.maxMultiple,
    ),
  );
  const rangeStyle = (min: number, max: number) =>
    ({
      "--range-start": `${((min - 1) / (scaleMax - 1)) * 100}%`,
      "--range-width": `${((max - min) / (scaleMax - 1)) * 100}%`,
    }) as CSSProperties;
  const formatMultiple = (value: number) => `${value.toFixed(2)}×`;

  return (
    <div className="photon-benchmark-instrument">
      <div className="photon-instrument-head mono">
        <span>{BENCH_ENV.gpu}</span>
        <span>{BENCH_ENV.workload}</span>
        <span>c{THROUGHPUT_RESULT.concurrency}</span>
      </div>
      <div className="photon-instrument-score">
        <span className="photon-instrument-number mono tabular">
          {THROUGHPUT_RESULT.cellsAhead}
          <small>/{THROUGHPUT_RESULT.cellsMatched}</small>
        </span>
        <span>
          <strong>Throughput tests led</strong>
          <small>Photon compared with each runtime</small>
        </span>
      </div>
      <div className="photon-instrument-ranges">
        {THROUGHPUT_RESULT.comparisons.map((comparison) => (
          <div className="photon-instrument-range" key={comparison.runtime}>
            <div className="photon-range-head">
              <span>{comparison.label}</span>
              <strong className="mono tabular">
                {formatMultiple(comparison.minMultiple)}–
                {formatMultiple(comparison.maxMultiple)}
              </strong>
            </div>
            <div className="photon-range-track" aria-hidden="true">
              <span
                className="photon-range-value"
                style={rangeStyle(
                  comparison.minMultiple,
                  comparison.maxMultiple,
                )}
              />
            </div>
            <div
              className="photon-test-cells"
              style={{
                gridTemplateColumns: `repeat(${comparison.cells}, minmax(2px, 1fr))`,
              }}
              aria-hidden="true"
            >
              {Array.from({ length: comparison.cells }, (_, index) => (
                <i key={index} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="photon-instrument-cold">
        <span className="mono">Cold start</span>
        <strong className="mono tabular">up to {coldStartMax} lower</strong>
        <p>{coldStartSummary}.</p>
      </div>
    </div>
  );
};

export function PhotonPage() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  const formatPercent = (value: number) =>
    value < 1 ? value.toFixed(1) : String(Math.round(value));
  const coldStartMaxLabel = `${formatPercent(COLD_START_RANGE.max)}%`;
  const coldStartSummary = `${COLD_STARTS.length} matched model/runtime comparisons`;

  return (
    <div className="md-landing">
      <Nav theme={theme} onToggleTheme={toggleTheme} />

      <main>
        <section className="photon-hero">
          <video
            className="photon-hero-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="/videos/photon-physical-ai-poster.jpg"
            aria-hidden="true"
          >
            {/* Pexels video 32386522, used under the Pexels license. */}
            <source src="/videos/photon-physical-ai.mp4" type="video/mp4" />
          </video>
          <div className="container photon-hero-inner">
            <span className="photon-hero-version mono">Photon 2.0</span>
            <h1>Photon</h1>
            <p className="photon-hero-line">Inference built for physical AI.</p>
            <p className="photon-hero-copy">
              Fast responses at small batch sizes, lower memory use, and local
              execution. Each model is{" "}
              <strong className="photon-hero-emphasis">compiled</strong> for the
              target GPU.
            </p>
            <div className="photon-hero-actions">
              <InstallSnippet />
              <a
                href={QUICKSTART_URL}
                target="_blank"
                rel="noreferrer"
                className="btn photon-hero-primary"
              >
                Quickstart <IconArrowUpRight size={14} />
              </a>
              <Link href={PERFORMANCE_URL} className="btn photon-hero-secondary">
                Performance <IconArrowRight size={14} />
              </Link>
              <Link href={SUPPORT_URL} className="btn photon-hero-secondary">
                Support <IconArrowRight size={14} />
              </Link>
              <Link href={UPDATES_URL} className="btn photon-hero-secondary">
                Updates <IconArrowRight size={14} />
              </Link>
            </div>
            <p className="photon-hero-proof mono">
              Moondream · Qwen · Gemma · NVIDIA H100
            </p>
          </div>
        </section>

        <section className="section photon-benchmark-band">
          <div className="container">
            <div className="photon-claim-grid">
              <div className="photon-claim-score mono tabular">
                <strong>{THROUGHPUT_RESULT.cellsAhead}</strong>
                <span>/{THROUGHPUT_RESULT.cellsMatched}</span>
                <small>throughput tests led</small>
              </div>
              <div className="photon-claim">
                <div
                  className="t-eyebrow"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {BENCH_ENV.label} throughput
                </div>
                <h2 className="t-heading-md" style={{ marginTop: 10 }}>
                  Higher throughput than vLLM and SGLang in matched H100 tests.
                </h2>
                <p
                  className="t-body-md"
                  style={{ color: "var(--text-secondary)", marginTop: 10 }}
                >
                  {THROUGHPUT_RESULT.comparisonSummary}. Photon led every test,
                  with up to {THROUGHPUT_RESULT.advantageMaxPct}% higher
                  throughput.
                </p>
                <div className="photon-claim-qual mono">
                  <span>
                    {THROUGHPUT_RESULT.gpu} · {THROUGHPUT_RESULT.workload} ·
                    concurrency {THROUGHPUT_RESULT.concurrency}
                  </span>
                  <span>
                    <Link href={PERFORMANCE_URL}>See benchmark data</Link> ·{" "}
                    <Link href={LAUNCH_URL}>Read the launch post</Link>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why physical AI needs a different engine */}
        <section className="section photon-physical-section">
          <div className="container">
            <div className="photon-physical-layout">
              <SectionHeader
                eyebrow="Built for physical AI"
                title="Live systems have different constraints."
                sub="Chat engines optimize large queues. Cameras, robots, and machines need quick responses from a few active streams, often on local hardware."
              />
              <PhysicalLoopVisual />
            </div>
            <div className="photon-operating-grid">
              {OPERATING_CHARACTERISTICS.map((c) => (
                <CharacteristicCard key={c.title} {...c} />
              ))}
            </div>
          </div>
        </section>

        {/* Supported today — model-family cards from the support dataset */}
        <section className="section" id="models">
          <div className="container photon-models-layout">
            <div className="photon-models-intro">
              <SectionHeader
                eyebrow="Models"
                title="Production-ready support."
                sub="Photon 2.0 supports Moondream, Qwen 3.5, and Gemma 4 on NVIDIA H100. More models and chips will follow."
              />
              <div className="photon-models-count mono tabular">
                <strong>{MODEL_FAMILIES.length}</strong>
                <span>model families · one runtime</span>
              </div>
            </div>
            <div className="model-card-grid photon-model-card-grid">
              {MODEL_FAMILIES.map((family, index) => (
                <FamilyCard key={family.id} family={family} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* How Photon works — the mechanism, after the outcome is clear */}
        <section className="section photon-compiler-section" id="how-it-works">
          <div className="container">
            <div className="photon-compiler-heading">
              <SectionHeader
                eyebrow="How Photon works"
                title="A megakernel for each model and GPU."
                sub="Photon compiles the model into a target-specific megakernel. The runtime handles requests, tokenization, and image decoding."
              />
              <MegakernelVisual />
            </div>

            <div className="photon-flow">
              {COMPILER_FLOW.map((node, i) => (
                <div className="photon-flow-item" key={node.title}>
                  {i > 0 ? (
                    <span className="flow-arrow" aria-hidden="true">
                      <IconArrowRight size={16} />
                    </span>
                  ) : null}
                  <div className={`flow-node ${node.core ? "flow-core" : ""}`}>
                    <span className="flow-k">{node.kicker}</span>
                    <span className="flow-t">{node.title}</span>
                    <span className="flow-d">{node.detail}</span>
                  </div>
                </div>
              ))}
            </div>
            <p
              className="t-caption mono"
              style={{ color: "var(--text-tertiary)", marginTop: 16 }}
            >
              One compiler path for each model, chip, and deployment goal.
            </p>
          </div>
        </section>

        {/* Performance preview — generated facts only */}
        <section className="section photon-performance-stage" id="performance">
          <div className="container photon-performance-layout">
            <div className="photon-performance-intro">
              <SectionHeader
                eyebrow="Performance"
                title="Faster execution. Lower startup time."
                sub="Measured on NVIDIA H100 against vLLM 0.25.1 and SGLang 0.5.3."
              />
              <Link
                href={PERFORMANCE_URL}
                className="btn photon-performance-link"
              >
                See the benchmark <IconArrowRight size={14} />
              </Link>
            </div>
            <BenchmarkInstrument
              coldStartMax={coldStartMaxLabel}
              coldStartSummary={coldStartSummary}
            />
          </div>
        </section>

        <section className="section" id="updates">
          <div className="container">
            <SectionHeader
              eyebrow="Updates"
              title="What changed."
              sub="New support, performance work, and fixes."
            />
            <div className="updates-list" style={{ marginTop: 40 }}>
              {UPDATES.slice(0, 3).map((change) => (
                <div className="updates-row" key={change.id}>
                  <span className="updates-date mono tabular">
                    {formatMatrixDate(change.date)}
                  </span>
                  <span className="updates-type">
                    <span className="tag">
                      {UPDATE_TYPE_LABELS[change.type]}
                    </span>
                  </span>
                  <div>
                    <div className="t-heading-sm">{change.title}</div>
                    <p
                      className="t-body-sm"
                      style={{ color: "var(--text-secondary)", marginTop: 4 }}
                    >
                      {change.impact}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 24 }}>
              <Link href={UPDATES_URL} className="family-cta">
                All updates <IconArrowRight size={13} />
              </Link>
            </div>
          </div>
        </section>

        <section className="section final-cta">
          <div className="container">
            <div className="final-card card">
              <div className="final-inner">
                <div>
                  <div
                    className="t-eyebrow"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    Your stack
                  </div>
                  <h2 className="t-heading-xl" style={{ marginTop: 10 }}>
                    Bring Photon to your workload.
                  </h2>
                  <p
                    className="t-body-lg"
                    style={{
                      color: "var(--text-secondary)",
                      marginTop: 12,
                      maxWidth: 620,
                    }}
                  >
                    Tell us which models, chips, and latency targets matter to
                    your deployment.
                  </p>
                </div>
                <div className="final-ctas">
                  <a
                    href="mailto:contact@moondream.ai"
                    className="btn btn-primary"
                  >
                    contact@moondream.ai <IconArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
